import { useEffect, useMemo, useState } from 'react';
import { PERIODS, REF_PERIOD, USAGES, fmt } from './domain';
import { isValidPerf, perfOf, setPerf } from './perf';
import { parseNum } from './sheet';
import { PC, UMR, caption } from './tokens';
import type { PeriodKey, RatioTable as Ratios, UsageKey } from './types';

interface Props {
  value: Ratios;
  /** Référentiel, pour signaler mes écarts. */
  defaults: Ratios;
  editing: boolean;
  onChange: (next: Ratios) => void;
  onValidityChange?: (valid: boolean) => void;
}

const th: React.CSSProperties = {
  padding: '6px 8px', height: 40, fontSize: 12, lineHeight: '16px', fontWeight: 700, color: UMR.textLabel,
  background: UMR.grey100, borderBottom: `1px solid ${UMR.border}`, textAlign: 'center', verticalAlign: 'middle',
};
const td: React.CSSProperties = {
  padding: '6px 8px', height: 34, fontSize: 13, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
  borderBottom: `1px solid ${UMR.borderSubtle}`,
};
const input: React.CSSProperties = {
  width: '100%', height: 28, background: UMR.white, border: `1px solid ${UMR.border}`,
  font: `400 13px/20px ${UMR.font}`, textAlign: 'right', padding: '0 6px',
  fontVariantNumeric: 'tabular-nums', color: UMR.black,
};

const key = (u: UsageKey, p: PeriodKey) => `${u}.${p}`;

/** Coefficients de performance par typologie × période, en % de la période de référence. */
export function PerfTable({ value, defaults, editing, onChange, onValidityChange }: Props) {
  const [texts, setTexts] = useState<Record<string, string>>({});
  useEffect(() => { if (!editing) setTexts({}); }, [editing]);

  const invalid = useMemo(
    () => Object.entries(texts).filter(([, t]) => !isValidPerf(parseNum(t))).map(([k]) => k),
    [texts],
  );
  useEffect(() => { onValidityChange?.(invalid.length === 0); }, [invalid.length, onValidityChange]);

  /** Contrôle non bloquant : les coefficients devraient décroître de gauche à droite. */
  const decreasing = useMemo(
    () => USAGES.every((u) => PERIODS.every((p, i) => i === 0 || perfOf(value, u.key, p.key) <= perfOf(value, u.key, PERIODS[i - 1].key))),
    [value],
  );

  const commit = (u: UsageKey, p: PeriodKey) => {
    const k = key(u, p);
    const raw = texts[k];
    if (raw === undefined) return;
    const n = parseNum(raw);
    if (!isValidPerf(n)) return;
    setTexts((t) => { const next = { ...t }; delete next[k]; return next; });
    if (n !== perfOf(value, u, p)) onChange(setPerf(value, u, p, n));
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontFamily: UMR.font }}>
      <colgroup>
        <col style={{ width: 170 }} />
        {PERIODS.map((p) => <col key={p.key} />)}
      </colgroup>
      <thead>
        <tr>
          <th style={{ ...th, textAlign: 'left' }}>Typologie</th>
          {PERIODS.map((p) => (
            <th key={p.key} style={{ ...th, color: p.key === REF_PERIOD ? UMR.textSecondary : undefined }}>
              {p.label}{p.key === REF_PERIOD ? ' (réf.)' : ''}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {USAGES.map((u) => (
          <tr key={u.key}>
            <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{u.label}</td>
            {PERIODS.map((p) => {
              const k = key(u.key, p.key);
              const current = perfOf(value, u.key, p.key);
              const base = perfOf(defaults, u.key, p.key);
              const changed = current !== base;
              const locked = p.key === REF_PERIOD;
              const text = texts[k] ?? String(current);
              const bad = invalid.includes(k);
              return (
                <td key={p.key} style={{ ...td, padding: editing && !locked ? 3 : td.padding, background: locked ? UMR.grey100 : !editing && changed ? PC.accentBg : undefined }}>
                  {editing && !locked ? (
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={`${u.label} — ${p.label}`}
                      value={text}
                      style={{ ...input, borderColor: bad ? PC.dangerText : UMR.border, color: changed ? PC.accentStrong : UMR.black }}
                      onChange={(ev) => setTexts((t) => ({ ...t, [k]: ev.target.value }))}
                      onBlur={() => commit(u.key, p.key)}
                      onKeyDown={(ev) => { if (ev.key === 'Enter') ev.currentTarget.blur(); }}
                    />
                  ) : (
                    <span style={{ color: locked ? UMR.textSecondary : changed ? PC.accentStrong : undefined, fontWeight: !locked && changed ? 700 : 400 }}>
                      {fmt(current, 1)} %
                    </span>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={PERIODS.length + 1} style={{ ...caption, padding: '6px 8px' }}>
            Coefficients de performance de chaque période de construction, comparés à la période de
            référence &lt; 1948 qui est à 100 %.
            {invalid.length > 0 && <span style={{ color: PC.dangerText }}> · {invalid.length} valeur(s) hors plage 1 – 100 %</span>}
            {invalid.length === 0 && !decreasing && <span style={{ color: PC.accentStrong }}> · certaines valeurs remontent d’une période à la suivante</span>}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}
